import { type KeyboardEvent } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Alert, Button, DividerCustom, Error, Typography } from 'shared/ui'
import { Warning } from 'shared/ui/Warning'
import { InputPassword } from 'shared/ui/TextField/InputPresets/InputPassword'
import {
  makeTextFieldAutoFocus,
  makeTextFieldHighlight,
  makeTextFieldHighlightLimit,
  TextField,
} from 'shared/ui/TextField'
import { Label } from 'shared/ui/Label'
import { LINK_PRIVACY_POLICY, LINK_TERMS_CONDITIONS } from 'shared/constants/links'
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from 'shared/constants/auth'
import { getAriaLabel } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type RegisterStore } from 'pages/register/ui/Register'
import { AuthPath } from 'pages/auth/route/authPath'
import styles from './styles.module.scss'

type IFormProps = {
  store: RegisterStore
}

export const SignUpForm = observer(({ store }: IFormProps) => {
  const { authStore } = useEntitiesContext()
  const {
    stepSignUpStore: {
      signUp,
      setEmail,
      email,
      setPassword,
      password,
      error,
      loading,
      blurPasswordField,
      focusPasswordField,
      isActivePasswordField,
      emailError,
      triggerEmailValidation,
      passwordError,
      triggerPasswordValidation,
      isMaxPassword,
      isValidEmail,
      isValidPassword,
      showConstantEmailError,
      showConstantPasswordError,
    },
    preloading,
  } = store

  const { googleAuthStore } = authStore

  const onKeyDownEmail = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      triggerEmailValidation()
      if (isValidEmail && document.activeElement instanceof HTMLElement)
        document.activeElement.blur()
    }
  }

  const onKeyDownPassword = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      triggerPasswordValidation()
      if (isValidPassword && document.activeElement instanceof HTMLElement)
        document.activeElement.blur()
    }
  }

  const onSignIn = () => {
    uiStore.changeRoute({
      path: `/${AuthPath.login}`,
    })
  }

  const handleSignUp = () => signUp()

  return (
    <div className={styles.wrap}>
      {error && (
        <Alert
          className={styles.error}
          item={{
            type: 'error',
            desc: error,
          }}
        />
      )}

      <Button
        className={styles.button}
        loading={googleAuthStore.loading}
        disabled={googleAuthStore.loading || loading || preloading}
        typeBtn='outlined'
        type='button'
        icon={'google'}
        text={'Sign up with Google'}
        contained={'secondary'}
        onClick={googleAuthStore.onAuth}
        fullWidth
      />

      <DividerCustom variant='text' className={styles.divider}>
        <Typography
          variant={'body-rg-regular'}
          ariaLabel={getAriaLabel('orDivider')}
          color={'var(--content-primary-tertiary)'}
        >
          OR
        </Typography>
      </DividerCustom>

      <Label
        label={{
          text: 'Email address',
          className: styles.inputLabel,
        }}
      />
      <TextField
        className={classNames(styles.textField, { [styles.error]: !!emailError })}
        variant='stroke'
        disabled={googleAuthStore.loading || loading || preloading}
        size='medium'
        error={!!emailError && showConstantEmailError}
        InputProps={{ placeholder: 'name@company.com', onKeyDown: onKeyDownEmail }}
        onChange={setEmail}
        value={email}
        mainActions={[
          makeTextFieldAutoFocus({ withFocus: true }),
          makeTextFieldHighlight({ mode: 'error', highlight: !!emailError }),
        ]}
      />
      <Error type={'text'} error={emailError} />

      <Label
        label={{
          text: 'Password',
          className: styles.inputLabel,
        }}
      />
      <TextField
        Input={InputPassword}
        className={classNames(styles.textField, {
          [styles.error]: !!passwordError || isMaxPassword,
        })}
        variant='stroke'
        onFocus={focusPasswordField}
        onBlur={blurPasswordField}
        error={!!passwordError && showConstantPasswordError}
        InputProps={{
          placeholder: `${MIN_PASSWORD_LENGTH}-${MAX_PASSWORD_LENGTH} characters`,
          onKeyDown: onKeyDownPassword,
        }}
        disabled={googleAuthStore.loading || loading || preloading}
        size='medium'
        onChange={setPassword}
        value={password}
        mainActions={[
          makeTextFieldHighlight({ mode: 'error', highlight: !!passwordError }),
          makeTextFieldHighlightLimit({ limit: MAX_PASSWORD_LENGTH }),
        ]}
      />
      <Error type={'text'} error={passwordError} />
      <Warning
        text={
          isMaxPassword && isActivePasswordField
            ? `Maximum password length is ${MAX_PASSWORD_LENGTH} characters`
            : ''
        }
      />

      <Button
        className={classNames(styles.button, styles.signUp)}
        typeBtn='contained'
        type='button'
        onClick={handleSignUp}
        loading={loading || preloading}
        disabled={googleAuthStore.loading || loading || preloading}
        text={'Sign up'}
        contained={'primary'}
        fullWidth
      />

      <div className={styles.termsAndPolicy}>
        <Typography variant={'body-sm-medium'} ariaLabel={'termsAndPolicy'}>
          {"By continuing, you agree to Salesmsg's "}
          <a target={'_blank'} href={LINK_TERMS_CONDITIONS} rel='noreferrer'>
            Terms of Service
          </a>{' '}
          and{' '}
          <a target={'_blank'} href={LINK_PRIVACY_POLICY} rel='noreferrer'>
            Privacy Policy
          </a>
        </Typography>
      </div>

      <div className={styles.bottom}>
        <Typography
          variant={'body-sm-medium'}
          ariaLabel={'signInLink'}
          color={'var(--content-primary-tertiary)'}
        >
          Already have a Salesmsg account?
          <Button onClick={onSignIn} typeBtn={'link'} text={'Sign in'} />
        </Typography>
      </div>
    </div>
  )
})

export default SignUpForm
